import React, { useEffect, useState } from "react";
import { X, Sparkles, Copy, Calendar as CalIcon, RefreshCw, Lock } from "lucide-react";
import clsx from "clsx";
import { toast } from "sonner";

import { aiApi, dailyNotesApi } from "@/lib/api";

/**
 * Daily Wrap drafter — on-demand narrative summary of a day's schedule,
 * blockers, drift, and outstanding items. PRD §8 report #9 + §9.10 module 7.
 *
 * v1 behaviour:
 *   - User picks a day (defaults to today).
 *   - Pressing GENERATE calls /api/projects/{id}/ai/daily-wrap.
 *   - Renders narrative + highlights / concerns / tomorrow lists.
 *   - "Copy" copies the full plain-text report to clipboard.
 *
 * "Lock canonical" (writing to daily_notes) is deferred — endpoint exists in
 * the schema but the canonical commit flow lives in §10 Phase 2 of the PRD.
 */
export default function DailyWrapDialog({ open, onClose, projectId, defaultDateKey }) {
  const [dateKey, setDateKey] = useState(defaultDateKey || todayKey());
  const [loading, setLoading] = useState(false);
  const [wrap, setWrap] = useState(null);
  const [locking, setLocking] = useState(false);

  useEffect(() => {
    if (open) {
      setDateKey(defaultDateKey || todayKey());
      setWrap(null);
    }
  }, [open, defaultDateKey]);

  useEffect(() => {
    if (!open) return;
    const onKey = (e) => {
      if (e.key === "Escape" && !loading) onClose();
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [open, onClose, loading]);

  if (!open) return null;

  const generate = async () => {
    if (!projectId || !dateKey || loading) return;
    setLoading(true);
    try {
      const r = await aiApi.dailyWrap(projectId, dateKey);
      setWrap(r);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to draft wrap.");
    } finally {
      setLoading(false);
    }
  };

  const copyToClipboard = () => {
    if (!wrap) return;
    const lines = [
      `DAILY WRAP — ${dateKey}`,
      "",
      wrap.narrative || "",
      "",
      "HIGHLIGHTS",
      ...(wrap.highlights || []).map((s) => `  • ${s}`),
      "",
      "CONCERNS",
      ...(wrap.concerns || []).map((s) => `  • ${s}`),
      "",
      "TOMORROW",
      ...(wrap.tomorrow || []).map((s) => `  • ${s}`),
    ];
    try {
      navigator.clipboard.writeText(lines.join("\n"));
      toast.success("Copied to clipboard");
    } catch {
      toast.error("Clipboard unavailable");
    }
  };

  const lockCanonical = async () => {
    if (!wrap || !projectId || locking) return;
    setLocking(true);
    try {
      await dailyNotesApi.lock(projectId, {
        date_key: dateKey,
        narrative: wrap.narrative || "",
        highlights: wrap.highlights || [],
        concerns: wrap.concerns || [],
        tomorrow: wrap.tomorrow || [],
      });
      toast.success(`Locked canonical wrap for ${dateKey}`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Lock failed");
    } finally {
      setLocking(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-[85] flex items-center justify-center bg-black/70 backdrop-blur-sm p-6"
      onClick={loading ? undefined : onClose}
      data-testid="daily-wrap-overlay"
    >
      <div
        className="w-[640px] max-w-full max-h-[90vh] bg-bg-surface border border-line-strong shadow-[0_24px_64px_rgba(0,0,0,0.8)] animate-slide-up flex flex-col"
        onClick={(e) => e.stopPropagation()}
        data-testid="daily-wrap-dialog"
      >
        <div className="flex items-center justify-between p-3 border-b border-line-strong bg-bg-elevated">
          <div className="flex items-center gap-2">
            <Sparkles className="w-3.5 h-3.5 text-violet" />
            <div>
              <div className="mono text-2xs uppercase tracking-[0.2em] text-ink-mute">DAILY WRAP</div>
              <div className="text-sm text-ink mt-0.5">AI-drafted narrative</div>
            </div>
          </div>
          <button onClick={onClose} disabled={loading} className="btn-ghost" data-testid="daily-wrap-close">
            <X className="w-3 h-3" />
          </button>
        </div>

        <div className="p-3 border-b border-line bg-bg-base flex items-center gap-2">
          <CalIcon className="w-3 h-3 text-ink-mute" />
          <input
            type="date"
            value={dateKey}
            onChange={(e) => setDateKey(e.target.value)}
            className="bg-bg-elevated text-xs p-1.5 outline-none border border-line focus:border-amber mono tabular-nums"
            data-testid="daily-wrap-date"
          />
          <button
            type="button"
            onClick={generate}
            disabled={loading || !projectId || !dateKey}
            className="btn-primary px-3"
            data-testid="daily-wrap-generate"
          >
            <RefreshCw className={clsx("w-3 h-3", loading && "animate-spin")} />
            {loading ? "DRAFTING…" : wrap ? "REGENERATE" : "GENERATE"}
          </button>
          {wrap && (
            <>
              <button
                type="button"
                onClick={lockCanonical}
                disabled={locking}
                className="btn-ghost ml-auto border border-amber/40 text-amber hover:bg-amber-glow"
                data-testid="daily-wrap-lock"
                title="Persist this wrap to daily_notes as the canonical record for this date"
              >
                <Lock className="w-3 h-3" /> {locking ? "LOCKING…" : "LOCK CANONICAL"}
              </button>
              <button
                type="button"
                onClick={copyToClipboard}
                className="btn-ghost"
                data-testid="daily-wrap-copy"
                title="Copy as plain text"
              >
                <Copy className="w-3 h-3" /> COPY
              </button>
            </>
          )}
        </div>

        <div className="flex-1 overflow-auto p-4 space-y-4">
          {!wrap && !loading && (
            <div className="text-ink-mute text-sm mono uppercase tracking-widest text-center py-12">
              Pick a date and press GENERATE.
            </div>
          )}

          {loading && (
            <div className="text-ink-mute text-sm mono uppercase tracking-widest text-center py-12 animate-pulse">
              Drafting wrap…
            </div>
          )}

          {wrap && (
            <>
              {wrap.totals && (
                <div className="grid grid-cols-4 gap-2 mono text-2xs" data-testid="daily-wrap-totals">
                  <Totals label="SCHEDULED" value={wrap.totals.scheduled} />
                  <Totals label="DONE" value={wrap.totals.done} accent="lime" />
                  <Totals label="IN PROG" value={wrap.totals.in_progress} accent="amber" />
                  <Totals label="DRIFT" value={`${wrap.totals.total_drift_min}m`} accent={wrap.totals.total_drift_min > 45 ? "red" : "amber"} />
                </div>
              )}

              <div className="border-l-2 border-violet pl-3" data-testid="daily-wrap-narrative">
                <div className="mono text-2xs uppercase tracking-[0.2em] text-violet mb-1.5">NARRATIVE</div>
                <p className="text-sm text-ink leading-relaxed whitespace-pre-wrap">
                  {wrap.narrative || "—"}
                </p>
              </div>

              <Section label="HIGHLIGHTS" items={wrap.highlights} accent="lime" testid="daily-wrap-highlights" />
              <Section label="CONCERNS" items={wrap.concerns} accent="amber" testid="daily-wrap-concerns" />
              <Section label="TOMORROW" items={wrap.tomorrow} accent="cyan" testid="daily-wrap-tomorrow" />
            </>
          )}
        </div>
      </div>
    </div>
  );
}

function Totals({ label, value, accent }) {
  const color = {
    lime: "text-lime",
    amber: "text-amber",
    red: "text-red",
  }[accent] || "text-ink";
  return (
    <div className="border border-line bg-bg-elevated p-2">
      <div className="text-ink-mute uppercase tracking-widest">{label}</div>
      <div className={clsx("text-base font-semibold tabular-nums mt-0.5", color)}>{value}</div>
    </div>
  );
}

function Section({ label, items, accent, testid }) {
  if (!items || items.length === 0) return null;
  const borderColor = {
    lime: "border-lime",
    amber: "border-amber",
    cyan: "border-cyan",
  }[accent] || "border-line";
  const textColor = {
    lime: "text-lime",
    amber: "text-amber",
    cyan: "text-cyan",
  }[accent] || "text-ink";
  return (
    <div className={clsx("border-l-2 pl-3", borderColor)} data-testid={testid}>
      <div className={clsx("mono text-2xs uppercase tracking-[0.2em] mb-1.5", textColor)}>{label}</div>
      <ul className="space-y-1">
        {items.map((s, i) => (
          <li key={`${i}-${s.slice(0, 24)}`} className="text-xs text-ink flex gap-2">
            <span className={clsx("mono shrink-0", textColor)}>—</span>
            <span className="flex-1">{s}</span>
          </li>
        ))}
      </ul>
    </div>
  );
}

function todayKey() {
  const d = new Date();
  const yyyy = d.getFullYear();
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  const dd = String(d.getDate()).padStart(2, "0");
  return `${yyyy}-${mm}-${dd}`;
}
